import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  AssignmentReturn as IndexIcon,
  Assignment as EntryIcon,
  Topic as MetadataIcon,
  ContentCopy as ContentIcon
} from '@mui/icons-material';
import { documentApi, knowledgeEntryApi, Document, KnowledgeEntry } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const DocumentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<Document | null>(null);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [indexLoading, setIndexLoading] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<number>(0);
  const [userRole, setUserRole] = useState<string>('');

  useEffect(() => {
    fetchDocument();
  }, [id]);

  // 获取当前用户信息
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setUserRole(user.role || '');
      } catch (error) {
        console.error('解析用户信息失败:', error);
      }
    }
  }, []);

  const fetchDocument = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!id) {
        setError('无效的文档ID');
        setLoading(false);
        return;
      }
      
      const documentId = parseInt(id);
      
      // 获取文档详情
      const documentData = await documentApi.getDocumentById(documentId);
      setDocument(documentData);
      
      // 获取文档的知识条目
      const entriesData = await knowledgeEntryApi.getEntriesByDocumentId(documentId);
      setEntries(entriesData);
      
      setLoading(false);
    } catch (err) {
      console.error('获取文档详情失败:', err);
      setError('获取文档详情失败，请重试');
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (document) {
        await documentApi.deleteDocument(document.id);
        setDeleteDialogOpen(false);
        navigate('/documents');
      }
    } catch (err) {
      console.error('删除文档失败:', err);
      alert('删除文档失败，请重试');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleIndexDocument = async () => {
    if (!document) return;
    
    try {
      setIndexLoading(true);
      await knowledgeEntryApi.indexDocument(document.id);
      alert('文档索引开始处理，请稍后查看结果');
      setIndexLoading(false);
      
      // 更新文档状态
      document.indexed = true;
      setDocument({ ...document });
    } catch (err) {
      console.error('索引文档失败:', err);
      alert('索引文档失败，请重试');
      setIndexLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="contained" 
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => navigate('/documents')}
        >
          返回文档列表
        </Button>
      </Box>
    );
  }

  if (!document) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">文档不存在或已被删除</Alert>
        <Button 
          variant="contained" 
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => navigate('/documents')}
        >
          返回文档列表
        </Button>
      </Box>
    );
  }

  return (
    <div className="page-container">
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          {document.title}
        </Typography>
        
        <Box>
          {!document.indexed && (
            <Button
              variant="contained"
              color="primary"
              startIcon={indexLoading ? <CircularProgress size={20} color="inherit" /> : <IndexIcon />}
              onClick={handleIndexDocument}
              disabled={indexLoading}
              sx={{ mr: 1 }}
            >
              索引文档
            </Button>
          )}
          {userRole === 'SUPER_ADMIN' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              删除文档
            </Button>
          )}
        </Box>
      </Box>

      {document.description && (
        <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
          <Typography variant="body1">
            {document.description}
          </Typography>
        </Paper>
      )}

      <Box sx={{ mb: 2 }}>
        <Chip 
          label={document.fileType} 
          color="primary" 
          variant="outlined"
          sx={{ mr: 1 }}
        />
        {document.category && (
          <Chip 
            label={document.category} 
            color="secondary" 
            variant="outlined"
            sx={{ mr: 1 }}
          />
        )}
        <Chip 
          label={document.indexed ? '已索引' : '未索引'} 
          color={document.indexed ? 'success' : 'warning'} 
        />
      </Box>

      <Paper sx={{ mb: 4 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="文档内容" icon={<ContentIcon />} />
          <Tab label="知识条目" icon={<EntryIcon />} />
          <Tab label="文档信息" icon={<MetadataIcon />} />
        </Tabs>

        {/* 文档内容面板 */}
        <TabPanel value={tabValue} index={0}>
          <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Typography 
              variant="body1" 
              component="pre" 
              sx={{ 
                whiteSpace: 'pre-wrap', 
                fontFamily: 'monospace', 
                fontSize: '0.9rem',
                lineHeight: 1.6
              }}
            >
              {document.content || '文档内容加载中...'}
            </Typography>
          </Paper>
        </TabPanel>

        {/* 知识条目面板 */}
        <TabPanel value={tabValue} index={1}>
          {entries.length > 0 ? (
            <Grid container spacing={2}>
              {entries.map((entry) => (
                <Grid item xs={12} key={entry.id}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>
                        {entry.content.length > 100 
                          ? `${entry.content.substring(0, 100)}...` 
                          : entry.content}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1">
                        {entry.content}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          位置: {entry.startPosition} - {entry.endPosition}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          创建于: {formatDate(entry.createdAt)}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary" align="center">
              {document.indexed 
                ? '没有找到知识条目' 
                : '文档尚未被索引，请先索引文档以创建知识条目'}
            </Typography>
          )}
        </TabPanel>

        {/* 文档信息面板 */}
        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">原始文件名</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{document.filename}</Typography>

                  <Typography variant="subtitle2">文件类型</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{document.fileType}</Typography>

                  <Typography variant="subtitle2">文件大小</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{formatFileSize(document.fileSize)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">上传者</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{document.uploadedBy}</Typography>

                  <Typography variant="subtitle2">上传时间</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{formatDate(document.createdAt)}</Typography>

                  <Typography variant="subtitle2">最后更新</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{formatDate(document.updatedAt)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      {/* 删除确认对话框 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            您确定要删除文档 "{document.title}" 吗？此操作不可撤销，相关的知识条目也将被删除。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            取消
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            删除
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DocumentView; 